import { type ReactNode, useState } from "react";
import { Check, ChevronDown, CircleSlash, Clock, ExternalLink, MapPin } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { COMMISSION_STATUS, day, dayLong, money, NA_REASON, relative, REVIEW, STAGE } from "./format";
import { Badge } from "./ui";
import type { ChecklistItem, RecruiterEventRow } from "./types";

/**
 * Tarjeta de un evento de la cartera con su checklist (Anexo 1). La usan:
 *   - "Mi trabajo" (el reclutador marca los ítems: `renderItemActions`),
 *   - la ficha del reclutador en Nexus (Mixcore revisa: `footer`).
 */
export function EventCard({
  ev,
  today,
  renderItemActions,
  footer,
  defaultOpen,
}: {
  ev: RecruiterEventRow;
  today: string;
  renderItemActions?: (item: ChecklistItem) => ReactNode;
  footer?: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  const review = REVIEW[ev.review.outcome] ?? REVIEW.pending;
  const isPast = ev.date < today;
  const isToday = ev.date === today;
  const p = ev.progress;

  return (
    <article className={cn("nx-card overflow-hidden", isToday && "border-accent")}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full text-left p-4 flex gap-3 items-start" aria-expanded={open}>
        <div className="shrink-0 w-12 text-center rounded-md bg-bg-subtle py-1.5">
          <div className="text-xs text-fg-muted uppercase">{day(ev.date).split(" ")[0]}</div>
          <div className="text-lg font-bold text-fg leading-none tabular-nums">{Number(ev.date.slice(8, 10))}</div>
          <div className="text-xs text-fg-muted">{day(ev.date).split(" ")[2]}</div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-fg truncate">{ev.name}</div>
          <div className="text-xs text-fg-muted truncate">
            {ev.producerName} · {ev.time ? `${ev.time} · ` : ""}
            {relative(ev.date, today)}
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {ev.canceled ? (
              <Badge tone="neutral">Cancelado</Badge>
            ) : (
              <>
                <Badge tone={ev.stage === "activation" ? "accent" : "info"}>
                  {STAGE[ev.stage]} · n.º {ev.number}
                  {ev.provisional ? " (si vende)" : ""}
                </Badge>
                <Badge tone={p.complete ? "success" : p.overdue ? "danger" : "neutral"}>
                  Checklist {p.done + p.na}/{p.total}
                </Badge>
                {(isPast || ev.review.status !== "pending") && <Badge tone={review.tone}>{review.label}</Badge>}
              </>
            )}
          </div>
        </div>
        <div className="shrink-0 flex flex-col items-end gap-1">
          {ev.commission && ev.commission.status !== "void" && (
            <span className="text-sm font-semibold tabular-nums text-fg">{money(ev.commission.amount)}</span>
          )}
          {ev.commission && (
            <span className="text-xs text-fg-subtle">{COMMISSION_STATUS[ev.commission.status].label}</span>
          )}
          <ChevronDown size={16} className={cn("text-fg-subtle transition-transform", open && "rotate-180")} />
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4 border-t border-border pt-4">
          {ev.review.status === "observed" && (
            <div className="rounded-md border border-warning/40 bg-warning-soft p-3 text-sm">
              <div className="font-medium text-warning">Mixcore observó este checklist</div>
              {ev.review.note && <p className="text-fg mt-1">{ev.review.note}</p>}
              {ev.review.cureDeadline && (
                <p className="text-xs text-fg-muted mt-1">
                  Plazo para subsanar: {dayLong(ev.review.cureDeadline)} ({relative(ev.review.cureDeadline, today)}).
                </p>
              )}
            </div>
          )}
          {ev.review.status === "retained" && ev.review.note && (
            <div className="rounded-md border border-danger/40 bg-danger-soft p-3 text-sm text-fg">{ev.review.note}</div>
          )}
          {ev.simultaneous && (
            <p className="text-xs text-fg-muted">
              Evento simultáneo con {ev.simultaneous.otherEventName}
              {ev.simultaneous.attendedEventId === ev.eventId ? ": asiste a este." : `: asiste al otro.`}
            </p>
          )}

          <ul className="space-y-2">
            {ev.items.map((it) => (
              <li key={it.key} className="flex gap-3 items-start">
                <ItemIcon item={it} today={today} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-fg">{it.label}</div>
                  <div className="text-xs text-fg-muted">{it.help}</div>
                  <div className="text-xs mt-0.5">
                    {it.status === "done" && <span className="text-success">Hecho{it.doneAt ? ` · ${dayLong(it.doneAt.slice(0, 10))}` : ""}</span>}
                    {it.status === "na" && <span className="text-fg-subtle">No aplica · {NA_REASON[it.naReason ?? ""] ?? ""}</span>}
                    {it.status === "pending" && it.dueDate && (
                      <span className={it.dueDate < today ? "text-danger" : "text-fg-subtle"}>
                        Hasta el {dayLong(it.dueDate)} ({relative(it.dueDate, today)})
                      </span>
                    )}
                    {it.note && <span className="text-fg-muted"> · “{it.note}”</span>}
                  </div>
                  {it.auto === "hub_ready" && ev.hubReady && (
                    <div className="text-xs mt-1 text-fg-muted">
                      Revisión automática:{" "}
                      {ev.hubReady.published ? "publicado" : "no publicado"} · {ev.hubReady.ticketTypes} tipo{ev.hubReady.ticketTypes === 1 ? "" : "s"} de entrada ·{" "}
                      {ev.hubReady.paidTickets} vendida{ev.hubReady.paidTickets === 1 ? "" : "s"}
                    </div>
                  )}
                  {renderItemActions && <ItemActions>{renderItemActions(it)}</ItemActions>}
                </div>
              </li>
            ))}
          </ul>

          {ev.report && (
            <div className="rounded-md bg-bg-subtle p-3 text-sm space-y-1.5">
              <div className="text-xs text-fg-muted">Reporte enviado el {dayLong(ev.report.submittedAt.slice(0, 10))}</div>
              {ev.report.worked && <p><b className="text-fg">Funcionó:</b> <span className="text-fg">{ev.report.worked}</span></p>}
              {ev.report.failed && <p><b className="text-fg">Falló:</b> <span className="text-fg">{ev.report.failed}</span></p>}
              {ev.report.producerAsked && <p><b className="text-fg">Pidió el productor:</b> <span className="text-fg">{ev.report.producerAsked}</span></p>}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2">
            <a href={ev.publicUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-accent hover:underline">
              <ExternalLink size={12} /> Ver la página del evento
            </a>
            {ev.review.tacitOn && ev.review.status === "pending" && (
              <span className="text-xs text-fg-subtle">Se aprueba solo el {dayLong(ev.review.tacitOn)} si Mixcore no lo observa.</span>
            )}
          </div>
          {footer}
        </div>
      )}
    </article>
  );
}

/** Botones de un ítem, debajo del texto (en el teléfono no le quitan ancho). */
function ItemActions({ children }: { children: ReactNode }) {
  if (!children) return null;
  return <div className="mt-2 flex flex-wrap gap-2">{children}</div>;
}

function ItemIcon({ item, today }: { item: ChecklistItem; today: string }) {
  if (item.status === "done") {
    return (
      <span className="mt-0.5 w-6 h-6 rounded-full bg-success-soft text-success flex items-center justify-center shrink-0">
        <Check size={14} />
      </span>
    );
  }
  if (item.status === "na") {
    return (
      <span className="mt-0.5 w-6 h-6 rounded-full bg-bg-subtle text-fg-subtle flex items-center justify-center shrink-0">
        <CircleSlash size={13} />
      </span>
    );
  }
  const late = !!item.dueDate && item.dueDate < today;
  return (
    <span className={cn("mt-0.5 w-6 h-6 rounded-full flex items-center justify-center shrink-0", late ? "bg-danger-soft text-danger" : "bg-bg-subtle text-fg-muted")}>
      {item.key === "presence" ? <MapPin size={13} /> : <Clock size={13} />}
    </span>
  );
}
